import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ChevronLeft, ChevronRight, FileDown, DollarSign, Clock, MapPin, Palmtree, Thermometer, Users, Settings, Save } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const MESI = ['', 'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];

const fmt = (v) => v.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function BustePagaPage() {
  const now = new Date();
  const [anno, setAnno] = useState(now.getFullYear());
  const [mese, setMese] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsForm, setSettingsForm] = useState({ moltiplicatore_straordinari: 1.25, indennita_trasferta: 50 });
  const [savingSettings, setSavingSettings] = useState(false);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/buste-paga/impostazioni`, { withCredentials: true });
      setSettingsForm({ moltiplicatore_straordinari: res.data.moltiplicatore_straordinari, indennita_trasferta: res.data.indennita_trasferta });
    } catch (err) {
      console.warn('Impostazioni buste paga non disponibili, uso default:', err?.message);
    }
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/buste-paga?anno=${anno}&mese=${mese}`, { withCredentials: true });
      setData(res.data);
    } catch (err) {
      console.error('Errore caricamento buste paga:', err);
      toast.error('Errore nel caricamento');
    }
    finally { setLoading(false); }
  }, [anno, mese]);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);
  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.put(`${API}/buste-paga/impostazioni`, settingsForm, { withCredentials: true });
      toast.success('Impostazioni salvate');
      setSettingsOpen(false);
      fetchData();
    } catch { toast.error('Errore nel salvataggio'); }
    finally { setSavingSettings(false); }
  };

  const prevMonth = () => { if (mese === 1) { setMese(12); setAnno(anno - 1); } else setMese(mese - 1); };
  const nextMonth = () => { if (mese === 12) { setMese(1); setAnno(anno + 1); } else setMese(mese + 1); };

  const handleDownloadPdf = async () => {
    try {
      const response = await axios.get(`${API}/buste-paga/pdf?anno=${anno}&mese=${mese}`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `buste_paga_${MESI[mese]}_${anno}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF scaricato');
    } catch { toast.error('Errore nel download'); }
  };

  if (loading && !data) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div></div>;

  const workers = data?.workers || [];
  const totCosto = workers.reduce((s, w) => s + w.totale_costo, 0);
  const totRicavo = workers.reduce((s, w) => s + w.totale_ricavo, 0);
  const totMargine = workers.reduce((s, w) => s + w.margine, 0);

  return (
    <div className="space-y-6" data-testid="buste-paga-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Buste Paga</h1>
          <p className="text-slate-500 mt-1">Riepilogo mensile costi e ricavi personale</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setSettingsOpen(!settingsOpen)} data-testid="toggle-settings-btn">
            <Settings className="h-4 w-4 mr-2" />Parametri
          </Button>
          <Button variant="outline" onClick={handleDownloadPdf} data-testid="download-buste-paga-pdf">
            <FileDown className="h-4 w-4 mr-2" />Scarica PDF
          </Button>
        </div>
      </div>

      {/* Parametri editabili */}
      {settingsOpen && (
        <Card className="border-blue-200 bg-blue-50/30" data-testid="settings-panel">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Settings className="h-5 w-5 text-blue-600" />Parametri Buste Paga
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-sm">Maggiorazione Straordinari (x)</Label>
                <Input
                  type="number" step="0.05" min="1" max="3"
                  value={settingsForm.moltiplicatore_straordinari}
                  onChange={(e) => setSettingsForm({...settingsForm, moltiplicatore_straordinari: parseFloat(e.target.value) || 1.25})}
                  data-testid="input-moltiplicatore"
                />
                <p className="text-xs text-slate-400">Es: 1.25 = maggiorazione del 25%</p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">Indennità Trasferta (€/giorno)</Label>
                <Input
                  type="number" step="1" min="0"
                  value={settingsForm.indennita_trasferta}
                  onChange={(e) => setSettingsForm({...settingsForm, indennita_trasferta: parseFloat(e.target.value) || 0})}
                  data-testid="input-indennita"
                />
                <p className="text-xs text-slate-400">Importo giornaliero per trasferta</p>
              </div>
            </div>
            <div className="flex justify-end mt-4">
              <Button onClick={handleSaveSettings} disabled={savingSettings} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-settings-btn">
                <Save className="h-4 w-4 mr-2" />{savingSettings ? 'Salvataggio...' : 'Salva Parametri'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Month Navigator */}
      <div className="flex items-center justify-center gap-4">
        <Button variant="ghost" size="icon" onClick={prevMonth} data-testid="bp-prev-month"><ChevronLeft className="h-5 w-5" /></Button>
        <h2 className="text-xl font-bold text-slate-900 min-w-[200px] text-center" data-testid="bp-current-month">{MESI[mese]} {anno}</h2>
        <Button variant="ghost" size="icon" onClick={nextMonth} data-testid="bp-next-month"><ChevronRight className="h-5 w-5" /></Button>
      </div>

      {/* Totali globali */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-slate-200 bg-slate-50">
          <CardContent className="p-4">
            <p className="text-xs text-slate-500 uppercase tracking-wider">Costo Totale Personale</p>
            <p className="text-2xl font-bold text-slate-900 mt-1" data-testid="totale-costo">{fmt(totCosto)}</p>
          </CardContent>
        </Card>
        <Card className="border-emerald-200 bg-emerald-50">
          <CardContent className="p-4">
            <p className="text-xs text-emerald-600 uppercase tracking-wider">Ricavo Totale</p>
            <p className="text-2xl font-bold text-emerald-700 mt-1" data-testid="totale-ricavo">{fmt(totRicavo)}</p>
          </CardContent>
        </Card>
        <Card className={`border-${totMargine >= 0 ? 'blue' : 'red'}-200 bg-${totMargine >= 0 ? 'blue' : 'red'}-50`}>
          <CardContent className="p-4">
            <p className={`text-xs ${totMargine >= 0 ? 'text-blue-600' : 'text-red-600'} uppercase tracking-wider`}>Margine</p>
            <p className={`text-2xl font-bold ${totMargine >= 0 ? 'text-blue-700' : 'text-red-700'} mt-1`} data-testid="totale-margine">{fmt(totMargine)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Info parametri */}
      {data && (
        <div className="flex items-center gap-4 text-xs text-slate-400 flex-wrap">
          <span>Maggiorazione straordinari: <strong>{data.moltiplicatore_straordinari}x</strong></span>
          <span>Indennità trasferta: <strong>{fmt(data.indennita_trasferta_giornaliera)}/gg</strong></span>
        </div>
      )}

      {/* Worker cards */}
      {workers.length === 0 ? (
        <p className="text-sm text-slate-400 text-center py-8">Nessun operaio attivo</p>
      ) : (
        <div className="space-y-4">
          {workers.map(w => (
            <Card key={w.worker_id} className="border-slate-200" data-testid={`busta-paga-${w.worker_id}`}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <div className="h-9 w-9 rounded-full bg-slate-200 flex items-center justify-center">
                      <span className="text-sm font-bold text-slate-600">{w.worker_name?.charAt(0)}</span>
                    </div>
                    <div>
                      <span className="block">{w.worker_name}</span>
                      <span className="text-xs text-slate-400 font-normal">{fmt(w.costo_orario)}/h costo | {fmt(w.prezzo_cliente)}/h cliente</span>
                    </div>
                  </CardTitle>
                  <div className="text-right">
                    <p className="text-lg font-bold text-slate-900" data-testid={`totale-${w.worker_id}`}>{fmt(w.totale_costo)}</p>
                    <p className="text-xs text-slate-400">costo totale</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-4 pt-0">
                {/* Ore e giorni */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                  <div className="p-2.5 rounded-lg bg-slate-50 border border-slate-200">
                    <div className="flex items-center gap-1.5 mb-1">
                      <Clock className="h-3.5 w-3.5 text-slate-500" />
                      <span className="text-[10px] text-slate-500 uppercase">Ore Ordinarie</span>
                    </div>
                    <p className="text-lg font-bold text-slate-900" data-testid={`ore-ord-${w.worker_id}`}>{w.ore_ordinarie}h</p>
                  </div>
                  <div className="p-2.5 rounded-lg bg-red-50 border border-red-200">
                    <div className="flex items-center gap-1.5 mb-1">
                      <Clock className="h-3.5 w-3.5 text-red-500" />
                      <span className="text-[10px] text-red-500 uppercase">Straordinari</span>
                    </div>
                    <p className="text-lg font-bold text-red-700" data-testid={`ore-str-${w.worker_id}`}>{w.ore_straordinarie}h</p>
                  </div>
                  <div className="p-2.5 rounded-lg bg-orange-50 border border-orange-200">
                    <div className="flex items-center gap-1.5 mb-1">
                      <MapPin className="h-3.5 w-3.5 text-orange-500" />
                      <span className="text-[10px] text-orange-500 uppercase">Trasferte</span>
                    </div>
                    <p className="text-lg font-bold text-orange-700" data-testid={`gg-trasf-${w.worker_id}`}>{w.giorni_trasferta}gg</p>
                  </div>
                  <div className="p-2.5 rounded-lg bg-blue-50 border border-blue-200">
                    <div className="flex items-center gap-1.5 mb-1">
                      <Users className="h-3.5 w-3.5 text-blue-500" />
                      <span className="text-[10px] text-blue-500 uppercase">Gg Lavorati</span>
                    </div>
                    <p className="text-lg font-bold text-blue-700">{w.giorni_lavorati}gg</p>
                  </div>
                </div>

                {/* Assenze */}
                {(w.giorni_ferie > 0 || w.giorni_malattia > 0 || w.giorni_permesso > 0) && (
                  <div className="flex items-center gap-2 mb-4 flex-wrap">
                    {w.giorni_ferie > 0 && <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200"><Palmtree className="h-3 w-3 mr-1" />Ferie: {w.giorni_ferie}gg</Badge>}
                    {w.giorni_malattia > 0 && <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200"><Thermometer className="h-3 w-3 mr-1" />Malattia: {w.giorni_malattia}gg</Badge>}
                    {w.giorni_permesso > 0 && <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200"><Clock className="h-3 w-3 mr-1" />Permessi: {w.giorni_permesso}gg</Badge>}
                  </div>
                )}

                {/* Dettaglio costi */}
                <div className="border-t border-slate-200 pt-3">
                  <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Costo ordinario</span>
                      <span className="font-medium text-slate-700">{fmt(w.costo_ordinario)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Ricavo ordinario</span>
                      <span className="font-medium text-emerald-700">{fmt(w.ricavo_ordinario)}</span>
                    </div>
                    {w.ore_straordinarie > 0 && (
                      <>
                        <div className="flex justify-between">
                          <span className="text-slate-500">Costo straordinari</span>
                          <span className="font-medium text-red-600">{fmt(w.costo_straordinari)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-500">Ricavo straordinari</span>
                          <span className="font-medium text-emerald-700">{fmt(w.ricavo_straordinari)}</span>
                        </div>
                      </>
                    )}
                    {w.giorni_trasferta > 0 && (
                      <div className="flex justify-between">
                        <span className="text-slate-500">Indennità trasferta</span>
                        <span className="font-medium text-orange-600">{fmt(w.indennita_trasferta)}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex justify-between items-center mt-3 pt-3 border-t border-slate-200">
                    <span className="font-semibold text-slate-700">Margine</span>
                    <span className={`text-lg font-bold ${w.margine >= 0 ? 'text-emerald-700' : 'text-red-700'}`} data-testid={`margine-${w.worker_id}`}>
                      {fmt(w.margine)}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
